import { useState } from "react";
import { useDropzone } from "react-dropzone";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Upload,
  FileSpreadsheet,
  CheckCircle,
  AlertCircle,
  Info,
  ArrowRight,
  Download,
  Eye,
} from "lucide-react";
import {
  ImportModelType,
  ImportPreviewData,
  ImportConfig,
} from "@/types/import";
import { parseExcelFile } from "@/utils/import-helpers/excel-parser";
import { importAPI } from "@/api/import";
import { useToast } from "@/hooks/use-toast";
import { Input } from "../ui/input";

type ImportStep = "upload-file" | "preview-data" | "import" | "complete";

interface ImportWizardProps {
  currentStep: ImportStep;
  selectedModel: ImportModelType;
  uploadedFile: File | null;
  previewData: ImportPreviewData | null;
  importConfig: ImportConfig;
  onStepChange: (step: ImportStep) => void;
  onFileUpload: (file: File) => void;
  onPreviewData: (data: ImportPreviewData) => void;
  onConfigChange: (config: ImportConfig) => void;
  onComplete: () => void;
  onViewHistory?: () => void;
  onImportComplete?: () => void;
}

const ImportWizard = ({
  currentStep,
  selectedModel,
  uploadedFile,
  previewData,
  importConfig,
  onStepChange,
  onFileUpload,
  onPreviewData,
  onConfigChange,
  onComplete,
  onViewHistory,
  onImportComplete,
}: ImportWizardProps) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [currentBatch, setCurrentBatch] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);
  const [importResult, setImportResult] = useState<any>(null);
  const { toast } = useToast();

  const onDrop = async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
      toast({
        title: "Invalid File Type",
        description: "Please upload an Excel file (.xlsx or .xls)",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "File size must be less than 10MB",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    try {
      onFileUpload(file);

      // Parse and validate the file
      const preview = await parseExcelFile(file, selectedModel);

      // Additional validation for chapters
      if (selectedModel === "chapters") {
        try {
          const chapterValidation = await importAPI.validateChapterDependencies(
            preview.rows
          );

          if (!chapterValidation.valid) {
            // Add chapter dependency errors to the validation
            const additionalErrors = chapterValidation.errors.map((error) => ({
              rowNumber: error.row,
              column: "Book Title / Language",
              value: `${error.bookTitle} (${error.language})`,
              errorType: "dependency" as const,
              message: error.message,
              suggestion:
                "Ensure the book exists and has summary content in the specified language",
            }));

            preview.validation.errors.push(...additionalErrors);
            preview.validation.validRowCount =
              preview.rows.length - preview.validation.errors.length;
            preview.validation.errorCount = preview.validation.errors.length;
          }
        } catch (validationError) {
          console.warn(
            "Chapter dependency validation failed:",
            validationError
          );
          // Don't fail the entire process, just log the warning
        }
      }

      // Additional validation for books
      if (selectedModel === "books") {
        try {
          const bookValidation = await importAPI.validateBookDependencies(
            preview.rows
          );

          if (!bookValidation.valid) {
            // Add book dependency errors to the validation as warnings
            const additionalWarnings = bookValidation.errors.map((error) => ({
              rowNumber: error.row,
              column: "Authors / Categories",
              value: [...error.missingAuthors, ...error.missingCategories].join(
                ", "
              ),
              warningType: "recommendation" as const,
              message: error.message,
            }));

            preview.validation.warnings.push(...additionalWarnings);
          }
        } catch (validationError) {
          console.warn("Book dependency validation failed:", validationError);
          // Don't fail the entire process, just log the warning
        }
      }

      onPreviewData(preview);

      toast({
        title: "File Processed Successfully",
        description: `Found ${preview.rows.length} rows with ${preview.validation.validRowCount} valid entries`,
      });

      onStepChange("preview-data");
    } catch (error) {
      toast({
        title: "File Processing Failed",
        description:
          error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-excel": [".xls"],
    },
    maxFiles: 1,
  });

  const handleStartImport = async () => {
    if (!previewData || !uploadedFile) return;

    setIsProcessing(true);
    setImportProgress(0);
    setCurrentBatch(0);
    setTotalBatches(0);

    try {
      const result = await importAPI.processImport(
        previewData,
        importConfig,
        uploadedFile.name,
        (progress, batch, total) => {
          setImportProgress(progress);
          setCurrentBatch(batch);
          setTotalBatches(total);
        }
      );

      setImportResult(result);

      toast({
        title: result.success
          ? "Import Completed Successfully"
          : "Import Completed with Errors",
        description: `${
          result.createdCount + result.updatedCount
        } rows processed successfully. ${result.errorCount} errors.`,
        variant: result.success ? "default" : "destructive",
      });

      // Notify that import is complete (for refreshing history)
      if (onImportComplete) {
        onImportComplete();
      }

      onStepChange("complete");
    } catch (error) {
      toast({
        title: "Import Failed",
        description:
          error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const renderUploadStep = () => (
    <div className="space-y-6 max-w-2xl mx-auto">
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-2">Upload Your Data File</h2>
        <p className="text-muted-foreground">
          Upload the Excel file with your {selectedModel} data
        </p>
      </div>

      <Card>
        <CardContent className="p-6">
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer
              ${
                isDragActive
                  ? "border-primary bg-primary/5"
                  : "border-muted-foreground/25 hover:border-primary hover:bg-primary/5"
              }`}
          >
            <input {...getInputProps()} />
            {isProcessing ? (
              <div className="space-y-4">
                <div className="animate-spin h-8 w-8 mx-auto border-2 border-primary border-t-transparent rounded-full" />
                <p className="text-sm text-muted-foreground">
                  Processing file...
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <Upload className="h-12 w-12 mx-auto text-muted-foreground" />
                <div>
                  <p className="text-lg font-medium">
                    {isDragActive
                      ? "Drop your file here"
                      : "Drag & drop your Excel file here"}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    or click to browse (Max 10MB, .xlsx or .xls)
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {uploadedFile && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              File Ready
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>File name:</span>
                <span className="font-medium">{uploadedFile.name}</span>
              </div>
              <div className="flex justify-between">
                <span>File size:</span>
                <span className="font-medium">
                  {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                </span>
              </div>
              <div className="flex justify-between">
                <span>Status:</span>
                <Badge variant="default">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Ready to process
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  const renderPreviewStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-2">Preview & Validate Data</h2>
        <p className="text-muted-foreground">
          Review your data and fix any issues before importing
        </p>
      </div>

      {previewData && (
        <div className="space-y-6">
          {/* Validation Summary */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">
                  Total Rows
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {previewData.validation.rowCount}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">
                  Valid Rows
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {previewData.validation.validRowCount}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Errors</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">
                  {previewData.validation.errors.length}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Validation Results */}
          {previewData.validation.errors.length > 0 && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Found {previewData.validation.errors.length} validation errors.
                Please fix these issues before proceeding with the import.
              </AlertDescription>
            </Alert>
          )}

          {previewData.validation.warnings.length > 0 && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Found {previewData.validation.warnings.length} warnings. These
                won't prevent import but should be reviewed.
              </AlertDescription>
            </Alert>
          )}

          {/* Data Preview Table */}
          <Card>
            <CardHeader>
              <CardTitle>Data Preview</CardTitle>
              <CardDescription>
                First 5 rows of your data (showing{" "}
                {Math.min(5, previewData.rows.length)} of{" "}
                {previewData.rows.length} rows)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-border">
                  <thead>
                    <tr className="bg-muted/50">
                      {previewData.headers.map((header) => (
                        <th
                          key={header}
                          className="border border-border p-2 text-left text-sm font-medium"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.rows.slice(0, 5).map((row, index) => (
                      <tr key={index} className="hover:bg-muted/25">
                        {previewData.headers.map((header) => (
                          <td
                            key={header}
                            className="border border-border p-2 text-sm"
                          >
                            {String(row[header] || "")}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={() => onStepChange("upload-file")}
            >
              Upload Different File
            </Button>

            <Button
              onClick={handleStartImport}
              disabled={
                previewData.validation.errors.length > 0 || isProcessing
              }
            >
              {isProcessing ? "Starting Import..." : "Start Import"}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  const renderImportStep = () => (
    <div className="space-y-6 max-w-2xl mx-auto text-center">
      <div>
        <h2 className="text-2xl font-bold mb-2">Import in Progress</h2>
        <p className="text-muted-foreground">
          Processing your {selectedModel} data...
        </p>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="animate-spin h-8 w-8 mx-auto border-2 border-primary border-t-transparent rounded-full" />
            <Progress value={importProgress} className="w-full" />
            <p className="text-sm text-muted-foreground">
              {totalBatches > 0
                ? `Processing batch ${currentBatch} of ${totalBatches}... (${Math.round(
                    importProgress
                  )}% complete)`
                : "Initializing import..."}
            </p>
          </div>
        </CardContent>
      </Card>

      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          This process may take a few minutes depending on the size of your
          data. Please don't close this page.
        </AlertDescription>
      </Alert>
    </div>
  );

  const renderCompleteStep = () => {
    // Determine actual import status based on results
    const totalSuccess =
      (importResult?.createdCount || 0) + (importResult?.updatedCount || 0);
    const hasErrors = (importResult?.errorCount || 0) > 0;
    const isCompleteSuccess = totalSuccess > 0 && !hasErrors;
    const isPartialSuccess = totalSuccess > 0 && hasErrors;
    const isCompleteFailure = totalSuccess === 0 && hasErrors;

    const getStatusIcon = () => {
      if (isCompleteSuccess) {
        return (
          <CheckCircle className="h-16 w-16 mx-auto text-green-500 dark:text-green-400 mb-4" />
        );
      } else if (isPartialSuccess) {
        return (
          <AlertCircle className="h-16 w-16 mx-auto text-yellow-500 dark:text-yellow-400 mb-4" />
        );
      } else {
        return (
          <AlertCircle className="h-16 w-16 mx-auto text-red-500 dark:text-red-400 mb-4" />
        );
      }
    };

    const getStatusTitle = () => {
      if (isCompleteSuccess) {
        return "Import Completed Successfully!";
      } else if (isPartialSuccess) {
        return "Import Completed with Issues";
      } else {
        return "Import Failed";
      }
    };

    const getStatusDescription = () => {
      if (isCompleteSuccess) {
        return `All ${selectedModel} data has been successfully processed`;
      } else if (isPartialSuccess) {
        return `Some ${selectedModel} data was processed, but there were errors`;
      } else {
        return `Failed to process ${selectedModel} data - please check the errors below`;
      }
    };

    return (
      <div className="space-y-6 max-w-2xl mx-auto text-center">
        <div>
          {getStatusIcon()}
          <h2 className="text-2xl font-bold mb-2 text-foreground">
            {getStatusTitle()}
          </h2>
          <p className="text-muted-foreground">{getStatusDescription()}</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Import Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2 text-sm">
              <div className="flex justify-between">
                <span>Total rows processed:</span>
                <span className="font-medium">
                  {importResult?.totalRows || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Successfully created:</span>
                <span className="font-medium text-green-500 dark:text-green-400">
                  {importResult?.createdCount || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Updated:</span>
                <span className="font-medium text-blue-500 dark:text-blue-400">
                  {importResult?.updatedCount || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Errors:</span>
                <span className="font-medium text-red-500 dark:text-red-400">
                  {importResult?.errorCount || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Duration:</span>
                <span className="font-medium">
                  {importResult?.duration
                    ? `${(importResult.duration / 1000).toFixed(1)}s`
                    : "N/A"}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {importResult?.errors && importResult.errors.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-red-500 dark:text-red-400">
                Import Errors
              </CardTitle>
              <CardDescription>
                The following errors occurred during import:
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="max-h-40 overflow-y-auto space-y-2">
                {importResult.errors
                  .slice(0, 5)
                  .map((error: any, index: number) => (
                    <div
                      key={index}
                      className="text-xs p-2 bg-red-50 dark:bg-red-950/20 rounded border-l-4 border-red-500 dark:border-red-400"
                    >
                      <span className="font-medium">Row {error.row}:</span>{" "}
                      {error.message}
                    </div>
                  ))}
                {importResult.errors.length > 5 && (
                  <p className="text-xs text-muted-foreground">
                    ... and {importResult.errors.length - 5} more errors
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="space-y-3">
          <Button onClick={onComplete} className="w-full">
            Start New Import
          </Button>
          <Button variant="outline" className="w-full" onClick={onViewHistory}>
            View Import History
          </Button>
        </div>
      </div>
    );
  };

  switch (currentStep) {
    case "upload-file":
      return renderUploadStep();
    case "preview-data":
      return renderPreviewStep();
    case "import":
      return renderImportStep();
    case "complete":
      return renderCompleteStep();
    default:
      return <div>Unknown step</div>;
  }
};

export default ImportWizard;
